import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { EnabledPOSTypes } from "@faire/retailer-visitor-shared/features/PointOfSale/components/EnabledPOSTypes";
import { SignUpElement } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { ConnectButton } from "@faire/retailer-visitor-shared/views/point-of-sale/landing-page/components/ConnectButton";
import { InventoryLevelsTable } from "@faire/retailer-visitor-shared/views/point-of-sale/landing-page/components/InventoryLevelsTable";
import { LandingPageContext } from "@faire/retailer-visitor-shared/views/point-of-sale/landing-page/components/LandingPageContext";
import { SECTION_PADDING_BREAKPOINT } from "@faire/retailer-visitor-shared/views/point-of-sale/landing-page/components/Section";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React, { useContext } from "react";
import styled from "styled-components";
export var ConnectHeroSection = function() {
    var isMobile = useViewport().isMobile;
    var isSSR = useIsSSR();
    var clickEvent = useContext(LandingPageContext).clickEvent;
    if (isMobile) {
        return /*#__PURE__*/ _jsxs(MobileContainer, {
            children: [
                /*#__PURE__*/ _jsx(CTAContainer, {
                    children: /*#__PURE__*/ _jsx(ConnectButton, {
                        onClick: function() {
                            return clickEvent === null || clickEvent === void 0 ? void 0 : clickEvent("top");
                        },
                        element: SignUpElement.HERO,
                        tooltipPosition: "bottom"
                    })
                }),
                /*#__PURE__*/ _jsxs(HeroImageInventoryContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(HeroBackgroundImage, {
                            $imageUrl: optimizeImageUrl({
                                url: "https://cdn.faire.com/fastly/9f96eeed866edf456557cf289f3d2aae890588a282beb6a6cde483475c006afd.png"
                            }, isSSR)
                        }),
                        /*#__PURE__*/ _jsx(InventoryLevelsContainer, {
                            children: /*#__PURE__*/ _jsx(InventoryLevelsTable, {
                                isSynced: true,
                                hideInventory: true,
                                showDivider: true
                            })
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(DesktopContainer, {
        children: [
            /*#__PURE__*/ _jsx(HalfWidthContainer, {
                children: /*#__PURE__*/ _jsxs(CTAContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(EnabledPOSTypes, {
                            includeText: true
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "3x"
                        }),
                        /*#__PURE__*/ _jsxs(Column, {
                            gap: {
                                mobileAndAbove: "3x",
                                tabletAndAbove: "4x"
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "displayLSerifRegular",
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "ie/8jr",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "Say goodbye to manual data entry"
                                            }
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(ConnectButton, {
                                    onClick: function() {
                                        return clickEvent === null || clickEvent === void 0 ? void 0 : clickEvent("top");
                                    },
                                    element: SignUpElement.HERO
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(HeroBackgroundImage, {
                justify: "flex-end",
                $imageUrl: optimizeImageUrl({
                    url: "https://cdn.faire.com/fastly/725638876b1b7fe3c576708898c8a43902c5d43cd0a0661cdd3e03c3f3fe0d0f.png"
                }, isSSR),
                children: [
                    /*#__PURE__*/ _jsx(InventoryLevelsContainer, {
                        children: /*#__PURE__*/ _jsx(InventoryLevelsTable, {
                            isSynced: true,
                            showDivider: true
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: 88
                    })
                ]
            })
        ]
    });
};
var MobileContainer = styled(Column).withConfig({
    componentId: "sc-f7432b58-0"
})([
    "background-color:",
    ";"
], Core.surface.inverse);
var DesktopContainer = styled(Row).withConfig({
    componentId: "sc-f7432b58-1"
})([
    "height:764px;background-color:",
    ";"
], Core.surface.inverse);
var HalfWidthContainer = styled(Column).attrs({
    align: "flex-end",
    justify: "center"
}).withConfig({
    componentId: "sc-f7432b58-2"
})([
    "width:50%;"
]);
var CTAContainer = styled(Column).withConfig({
    componentId: "sc-f7432b58-3"
})([
    "max-width:584px;width:100%;padding:",
    ";@media (min-width:",
    "px){padding:",
    ";}",
    "{padding:",
    ";}",
    "{padding:",
    ";}"
], getSpacing(0, "8x", 0, "5x"), SECTION_PADDING_BREAKPOINT, getSpacing(0, "10x", 0, 0), responsiveMediaQueries.tablet, getSpacing(0, "8x", 0, "3x"), responsiveMediaQueries.mobile, getSpacing("3x", "2x"));
var HeroBackgroundImage = styled(Column).withConfig({
    componentId: "sc-f7432b58-4"
})([
    "width:50%;height:100%;background-image:url(",
    ");background-repeat:no-repeat;background-size:cover;background-position:top center;user-select:none;",
    "{width:100%;height:282px;}"
], function(props) {
    return props.$imageUrl;
}, responsiveMediaQueries.mobile);
var InventoryLevelsContainer = styled(Column).withConfig({
    componentId: "sc-f7432b58-5"
})([
    "width:fit-content;height:304px;margin:0 0 0 ",
    ";",
    "{position:absolute;height:184px;left:",
    ";bottom:",
    ";margin:0;}"
], getSpacing("-6x"), responsiveMediaQueries.mobile, getSpacing("2x"), getSpacing("2x"));
var HeroImageInventoryContainer = styled(Column).withConfig({
    componentId: "sc-f7432b58-6"
})([
    "position:relative;width:100%;height:338px;background-color:",
    ";"
], Core.surface.subdued);
